import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FiX } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { getTrackingData } from "../../utils/trackingConfig";
import { subscriptionData } from "../../redux/slices/subscriptionSlice";
import { subscriptionPayment } from "../../redux/slices/subscriptionPaymentSlice";
// Reuse the upgrade-pro-modal styling — same layout, colours, sizes
// and mobile bottom-sheet behaviour, only the icon / copy change.
import "./ComparisonModal.scss";

const SWIPE_CLOSE_THRESHOLD = 110;
const SWIPE_MOBILE_BREAKPOINT = 576;

/* Icon variants — pick via the `iconType` prop. Keep all variants here so
   call sites stay simple and the modal owns its visuals. */
const SearchPinIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="10.5" cy="10.5" r="6.5" />
    <line x1="20" y1="20" x2="15.2" y2="15.2" />
    <path d="M10.5 13.2c1.5-1.7 2.6-3 2.6-4.4a2.6 2.6 0 1 0-5.2 0c0 1.4 1.1 2.7 2.6 4.4z" />
    <circle cx="10.5" cy="8.8" r="0.9" fill="currentColor" stroke="none" />
  </svg>
);

const UserFollowIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="19" y1="8" x2="19" y2="14" />
    <line x1="22" y1="11" x2="16" y2="11" />
  </svg>
);

const WalletIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M3 7a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2v2H6a1 1 0 0 0 0 2h15v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
    <circle cx="17" cy="14" r="1.4" fill="currentColor" stroke="none" />
  </svg>
);

const ClipboardDollarIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="5" y="4" width="14" height="18" rx="2" />
    <rect x="9" y="2" width="6" height="4" rx="1" />
    <path d="M12 10v6" />
    <path d="M14 11.5c0-.8-.7-1.5-1.5-1.5h-1c-.8 0-1.5.7-1.5 1.5s.7 1.5 1.5 1.5h1c.8 0 1.5.7 1.5 1.5s-.7 1.5-1.5 1.5h-1c-.8 0-1.5-.7-1.5-1.5" />
  </svg>
);

const CalculatorIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="5" y="3" width="14" height="18" rx="2" />
    <rect x="7.5" y="6" width="9" height="3" rx="0.6" />
    <circle cx="8.5" cy="12.5" r="0.6" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12.5" r="0.6" fill="currentColor" stroke="none" />
    <circle cx="15.5" cy="12.5" r="0.6" fill="currentColor" stroke="none" />
    <circle cx="8.5" cy="16" r="0.6" fill="currentColor" stroke="none" />
    <circle cx="12" cy="16" r="0.6" fill="currentColor" stroke="none" />
    <circle cx="15.5" cy="16" r="0.6" fill="currentColor" stroke="none" />
  </svg>
);

const BookmarkIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </svg>
);

const ICONS = {
  "search-pin": SearchPinIcon,
  "user-follow": UserFollowIcon,
  wallet: WalletIcon,
  "clipboard-dollar": ClipboardDollarIcon,
  calculator: CalculatorIcon,
  bookmark: BookmarkIcon,
};

const SparkIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 2 13.6 8.4 20 10l-6.4 1.6L12 18l-1.6-6.4L4 10l6.4-1.6z" />
  </svg>
);

const DEFAULTS = {
  heading: "Want to view more city details?",
  subText:
    "Upgrade to Pro to explore detailed city information without limits.",
  infoText:
    "Pro users can view all cities and access detailed information without limits.",
  trialPrice: "$2.99/month",
  yearlyPrice: "$29.99/year",
};

// Third-CTA presets — selected via the `thirdCtaMode` prop. Callers can still
// override the question / label individually with the existing props.
const THIRD_CTA_PRESETS = {
  restore: {
    question: "Want jobs in your inbox too?",
    label: "Get Pro + Newsletter, $4.99/month billed annually at $49.99/year",
  },
  newsletter: {
    question: "Want jobs in your inbox too?",
    label: "Get Pro + Newsletter, $4.99/month billed annually at $49.99/year",
  },
};

const RestrictionModal = ({
  isVisible,
  onClose,
  eventSource,
  // optional overrides — fall back to the default load-more-city copy
  iconType,
  heading,
  subText,
  infoText,
  yearlyPrice,
  trialPrice,
  // Third-CTA: pick a preset with `thirdCtaMode` ("restore" | "newsletter")
  // or override the text directly with restoreQuestion / restoreLabel.
  thirdCtaMode,
  restoreQuestion,
  restoreLabel,
  // When the user is already on the top paid plan (e.g. Pro hitting the
  // hard scenario cap), suppress every upgrade/trial CTA. The modal then
  // shows heading + sub + info + a single dismiss button — purely a
  // status/help message, no upsell.
  hideUpgradeCtas = false,
  // Label for the dismiss button. Defaults to the upsell-friendly
  // "Maybe later"; callers with hideUpgradeCtas usually want "Got it".
  closeLabel,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth?.user);
  const profileStore = useSelector((state) => state.profile);
  const subscriptionState = useSelector((state) => state.subscriptionData);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const hasUsedProTrial =
    user?.has_used_pro_trial ??
    profileStore?.profileItem?.data?.has_used_pro_trial ??
    false;

  const thirdCtaPreset =
    THIRD_CTA_PRESETS[thirdCtaMode] || THIRD_CTA_PRESETS.restore;

  const finalHeading = heading || DEFAULTS.heading;
  const finalSub = subText || DEFAULTS.subText;
  const finalInfo = infoText || DEFAULTS.infoText;
  const finalYearly = yearlyPrice || DEFAULTS.yearlyPrice;
  const finalTrial = trialPrice || DEFAULTS.trialPrice;
  const finalRestoreQuestion = restoreQuestion || thirdCtaPreset.question;
  const finalRestoreLabel = restoreLabel || thirdCtaPreset.label;
  const IconComponent = ICONS[iconType] || SearchPinIcon;

  /* Mobile-only swipe-down-to-close (same gesture as ComparisonModal). */
  const dialogRef = useRef(null);
  const startYRef = useRef(null);
  const draggingRef = useRef(false);
  const [dragY, setDragY] = useState(0);

  const isMobile = () =>
    typeof window !== "undefined" &&
    window.innerWidth <= SWIPE_MOBILE_BREAKPOINT;

  const onTouchStart = (e) => {
    if (!isMobile()) return;
    startYRef.current = e.touches[0].clientY;
    draggingRef.current = true;
  };
  const onTouchMove = (e) => {
    if (!draggingRef.current || startYRef.current == null) return;
    const dy = e.touches[0].clientY - startYRef.current;
    setDragY(dy > 0 ? dy : 0);
  };
  const onTouchEnd = () => {
    if (!draggingRef.current) return;
    const finalDy = dragY;
    draggingRef.current = false;
    startYRef.current = null;
    if (finalDy > SWIPE_CLOSE_THRESHOLD) {
      setDragY(0);
      onClose && onClose();
    } else {
      setDragY(0);
    }
  };

  useEffect(() => {
    if (!isVisible) setDragY(0);
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    const content = document.querySelector(".upgrade-pro-modal .modal-content");
    if (!content) return;
    if (dragY > 0) {
      content.style.transform = `translateY(${dragY}px)`;
      content.style.transition = "none";
    } else {
      content.style.transform = "";
      content.style.transition = "";
    }
  }, [dragY, isVisible]);

  const handleUpgrade = () => {
    onClose && onClose();
    const { event } = getTrackingData(eventSource);
    navigate("/profile/subscription", { state: { source: eventSource } });
  };

  const handleSkipTrial = async () => {
    let plans = subscriptionState?.subscriptionDataItems || [];
    if (!plans.length) {
      try {
        const result = await dispatch(subscriptionData()).unwrap();
        plans = result?.subscriptionData || [];
      } catch (e) {
        handleUpgrade();
        return;
      }
    }
    const proMonthlyPlan = plans.find(
      (p) =>
        p.name === "Pro" &&
        p.interval === "month" &&
        p.slug !== "pro-newsletter",
    );
    if (!proMonthlyPlan?.price_id) {
      handleUpgrade();
      return;
    }
    const { event } = getTrackingData(eventSource);
    const payload = {
      priceId: proMonthlyPlan.price_id,
      customerEmail: user?.email,
      metadata: {
        userId: user?.id,
        plan_id: proMonthlyPlan.id,
        billingCycle: "month",
        trigger_feature: event,
      },
    };
    setIsCheckingOut(true);
    try {
      const result = await dispatch(subscriptionPayment(payload)).unwrap();
      const checkoutUrl =
        result?.subscriptionPaymentItems?.url ||
        result?.url ||
        result?.data?.url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        handleUpgrade();
      }
    } catch (error) {
      console.error("Skip trial checkout failed:", error);
      handleUpgrade();
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleTrial = () => {
    onClose && onClose();
    navigate("/profile/subscription", {
      state: { source: eventSource, trial: true },
    });
  };

  const handleThirdCta = () => {
    onClose && onClose();
    const stateExtra =
      thirdCtaMode === "newsletter" ? { newsletter: true } : { restore: true };
    navigate("/profile/subscription", {
      state: { source: eventSource, ...stateExtra },
    });
  };

  return (
    <Modal
      show={isVisible}
      onHide={onClose}
      centered
      backdrop={true}
      dialogClassName="upgrade-pro-modal"
      backdropClassName="upgrade-pro-modal-backdrop"
    >
      <Modal.Body
        ref={dialogRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        <div
          className="upm-handle"
          aria-hidden="true"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
        />

        <button
          type="button"
          className="upm-close"
          onClick={onClose}
          aria-label="Close"
        >
          <FiX size={20} />
        </button>

        <div className="upm-icon-row">
          <div className="upm-icon">
            <IconComponent className="upm-icon-svg" />
          </div>
        </div>

        <h2 className="upm-title">{finalHeading}</h2>
        <p className="upm-sub">{finalSub}</p>

        <div className="upm-info">
          <SparkIcon className="upm-info-spark" />
          <p className="upm-info-text">{finalInfo}</p>
        </div>

        {!hideUpgradeCtas && (
          <div className="upm-ctas">
            {!hasUsedProTrial ? (
            <button
              type="button"
              className="upm-btn-primary"
              onClick={handleTrial}
            >
              <span className="upm-btn-primary-title">Start 3-day free trial</span>
              <span className="upm-btn-primary-sub">
                No charge today. {finalYearly} after your 3-day trial.
              </span>
            </button>
            ) : (
            <button
              type="button"
              className="upm-btn-primary"
              onClick={handleTrial}
            >
              <span className="upm-btn-primary-title">Get Pro</span>
              <span className="upm-btn-primary-sub">
                {finalTrial}, billed annually at {finalYearly}. Cancel anytime.
              </span>
            </button>
            )}

            <button
              type="button"
              className="upm-btn-secondary"
              onClick={handleSkipTrial}
              disabled={isCheckingOut}
            >
              {isCheckingOut
                ? "Opening Secure Payment..."
                : hasUsedProTrial
                ? `Buy Pro Plan · ${finalTrial}`
                : `Buy Pro Plan · ${finalTrial}`}
            </button>

            <button
              type="button"
              className="upm-newsletter"
              onClick={handleThirdCta}
            >
              <span className="upm-newsletter-q">{finalRestoreQuestion}</span>
              <span className="upm-newsletter-cta">{finalRestoreLabel}</span>
            </button>
          </div>
        )}

        <button type="button" className="upm-later" onClick={onClose}>
          {closeLabel || (hideUpgradeCtas ? "Got it" : "Maybe later")}
        </button>
      </Modal.Body>
    </Modal>
  );
};

export default RestrictionModal;
